import React, { FC, useCallback, useEffect, useRef, useState } from 'react';
import videojs, { VideoJsPlayer, VideoJsPlayerOptions } from 'video.js';
import getConfig from 'next/config';
import 'videojs-mux';
import 'videojs-contrib-quality-levels';
import { MuxDataOptions } from 'typings/videojs-mux';
import { logEvent } from 'utils/eventTracking';

const { publicRuntimeConfig } = getConfig();

export const useVideoJS = (
  videoJsOptions: VideoJsPlayerOptions,
  eventHandlers: { usage: (e: unknown) => void },
) => {
  const { usage } = eventHandlers;
  const videoNode = useRef<HTMLVideoElement>(null);
  const [ready, setReady] = useState(false);
  const player = useRef<VideoJsPlayer | null>(null);
  useEffect(() => {
    if (videoNode.current) {
      player.current = videojs(videoNode.current, {
        controls: true,
        muted: true,
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore incorrect typings for the controlBar options
        controlBar: {
          pictureInPictureToggle: true,
        },
        ...videoJsOptions,
      });
      player.current.ready(() => {
        player.current?.tech().on('usage', usage);
        const promise = player.current?.play();
        if (promise !== undefined) {
          promise
            .then(() => {
              // Autoplay started!
              player.current?.muted(false);
            })
            .catch(() => {
              // Autoplay was prevented.
            });
        }
        setReady(true);
      });
    }
  }, [videoJsOptions, usage]);

  const Video = useCallback(
    () => (
      <div data-vjs-player style={{ width: '100%', height: '100%' }}>
        <video ref={videoNode} className="video-js vjs-big-play-centered" />
      </div>
    ),
    [],
  );
  return { Video, ready, player: player.current, videoNode };
};

const usePlayer = ({
  src,
  options,
  eventHandlers,
  muxMetaData,
}: {
  src: string;
  options?: VideoJsPlayerOptions;
  eventHandlers?: {
    ended: (e) => void;
    errorHandler?: (mediaError: MediaError | null) => void;
    // eslint-disable-next-line @typescript-eslint/no-shadow
    retryplaylist?: ({ playerTimeout: boolean, player: VideoJsPlayer }) => void;
  };
  muxMetaData: Omit<
    MuxDataOptions,
    'video_duration' | 'player_init_time' | 'env_key' | 'video_encoding_variant'
  >;
}) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [player, setPlayer] = useState<VideoJsPlayer>();

  // eslint-disable-next-line consistent-return
  useEffect(() => {
    if (videoRef.current) {
      const vjsPlayer = videojs(videoRef.current, {
        controls: true,
        fluid: true,
        aspectRatio: '16:9',
        inactivityTimeout: 0,
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore incorrect typings for the controlBar options
        controlBar: {
          pictureInPictureToggle: true,
          remainingTimeDisplay: false,
          progressControl: {
            seekBar: false,
          },
        },
        ...options,
      });
      setPlayer(vjsPlayer);
    }
  }, [options, player]);

  // eslint-disable-next-line consistent-return
  useEffect(() => {
    if (player) {
      // @ts-ignore
      player?.reloadSourceOnError({
        // getSource allows you to override the source object used when an error occurs
        // getSource: reload => {
        //   console.log('Reloading because of an error');

        //   // call reload() with a fresh source object
        //   // you can do this step asynchronously if you want (but the error dialog will
        //   // show up while you're waiting)
        //   reload({
        //     src,
        //     type: 'application/x-mpegURL',
        //   });
        // },

        // errorInterval specifies the minimum amount of seconds that must pass before
        // another reload will be attempted
        errorInterval: 5,
      });
      const internalErrorHandler = () => {
        const playerError = player.error();
        if (eventHandlers?.errorHandler) {
          eventHandlers.errorHandler(playerError);
        }
      };
      let retry = 0;
      const retryplaylistHandler = () => {
        retry += 1;
        if (retry >= 5) {
          if (eventHandlers?.retryplaylist) {
            eventHandlers.retryplaylist({ playerTimeout: true, player });
          }
        }
      };
      player.ready(() => {
        const playerInitTime = Date.now();
        if (eventHandlers) {
          player.on('ended', eventHandlers.ended);
          player.on('error', internalErrorHandler);
          player.tech(true).on('retryplaylist', retryplaylistHandler);
        }
        player.src({ src, type: 'application/x-mpegURL' });
        player.mux({
          debug: false,
          data: {
            env_key: publicRuntimeConfig.MUX_ENV_KEY,
            // Metadata
            player_name: muxMetaData.player_name || 'RTMP player',
            viewer_user_id: muxMetaData.viewer_user_id,
            video_id: muxMetaData.video_id,
            video_title: muxMetaData.video_title,
            video_duration: player.duration(),
            player_init_time: playerInitTime,
            // @ts-ignore
            video_stream_type: 'live',
          },
        });
        if (options?.autoplay) {
          const playPromise = player.play();
          if (playPromise !== undefined) {
            playPromise
              .then(() => {
                // Autoplay started!
              })
              .catch(_ => {
                // Autoplay was prevented.
                player.muted(true);
                player.play();
              });
          }
        }
      });
      return () => {
        if (eventHandlers) {
          player.off('ended', eventHandlers.ended);
          player.off('error', internalErrorHandler);
          player.tech(true).off('retryplaylist', retryplaylistHandler);
        }
      };
    }
  }, [
    player,
    src,
    eventHandlers,
    options?.autoplay,
    muxMetaData.viewer_user_id,
    muxMetaData.video_id,
    muxMetaData.video_title,
    muxMetaData.player_name,
  ]);

  return videoRef;
};

export const VideoPlayerRTMP: FC<{
  src: string;
  sessionId: string;
  options?: VideoJsPlayerOptions;
  eventHandlers?: {
    ended: (e) => void;
    errorHandler?: (mediaError: MediaError | null) => void;
    // eslint-disable-next-line @typescript-eslint/no-shadow
    retryplaylist?: ({ playerTimeout: boolean, player: VideoJsPlayer }) => void;
  };
  muxMetaData: Omit<
    MuxDataOptions,
    'video_duration' | 'player_init_time' | 'env_key' | 'video_encoding_variant'
  >;
}> = props => {
  const { src, options, eventHandlers, muxMetaData, sessionId } = props;
  const playerRef = usePlayer({ src, options, eventHandlers, muxMetaData });

  useEffect(() => {
    logEvent('HYBRID_LIVESTREAM_STARTS_USER', {
      session_id: sessionId,
    });
  }, [sessionId]);

  return (
    <div data-vjs-player>
      <video ref={playerRef} className="video-js vjs-big-play-centered" />
    </div>
  );
};
